"use client"

import * as React from "react"
import {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnSizingState,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table"
import { isAllSelected } from "@/lib/view-preferences/filter-sentinel"
import type { ColumnDefaults, PersistedTableState } from "./types"

/**
 * Drop any facet whose RESTORED selection carries the "Select All" sentinel.
 *
 * `toColumnFilterValue` (unified-data-table.tsx) keeps the sentinel off the
 * live click path by expanding `["__ALL__"]` into every option value. Nothing
 * guarded the restore paths, and three of them can hand the raw token back
 * into `columnFilters`: the `<prefix>-column-filters` localStorage row (also
 * written by page-level hooks that own the same key — use-recipients-page
 * persists URL-hydrated filters and forwards `["__ALL__"]` verbatim for the
 * Tags facet), the `ai-nav-column-filters` sessionStorage handoff, and the
 * cross-component `table-column-filter-change` / `ai-nav-context` events.
 *
 * The token is not a value any row holds, so a replayed one matches NOTHING:
 * a user who ticked "Select All" once got an empty table on every later visit,
 * and the token was re-saved on each mount, so it never aged out.
 *
 * The facet is dropped rather than kept, per the sentinel's own contract —
 * "every value selected" === "no filter on this facet". Expanding it here is
 * not possible: the hook never sees the option lists.
 *
 * RETURNS THE INPUT ARRAY UNCHANGED when there is nothing to strip, and that
 * is load-bearing, not an optimisation. The `table-column-filter-change`
 * listener below feeds the payload straight back into `setColumnFilters`, and
 * the effect that writes localStorage DISPATCHES that same event with the
 * current state. A fresh array on every pass defeats React's `Object.is`
 * bail-out, so state → effect → event → state loops forever ("Maximum update
 * depth exceeded"). Guarded by the "returns the SAME array reference" case in
 * __tests__/persistence-strips-select-all-sentinel.spec.tsx, asserted on this
 * helper directly: through the hook the regression shows up as the suite
 * HANGING to Jest's timeout, which reads like machine load rather than a bug.
 */
export function stripSelectAllSentinel(
  filters: ColumnFiltersState,
): ColumnFiltersState {
  if (!Array.isArray(filters)) return filters
  const kept = filters.filter(
    (f) => !(Array.isArray(f?.value) && isAllSelected(f.value as string[])),
  )
  return kept.length === filters.length ? filters : kept
}

interface UseTablePersistenceConfig {
  prefix: string
  columnDefaults?: ColumnDefaults
  filterDefaults?: { visibility?: Record<string, boolean>; order?: string[] }
  defaultSorting?: SortingState
  /**
   * When true, sorting is owned by the parent (server-side, via the
   * `onSortChange` callback + a URL/state query param). The localStorage
   * `${prefix}-sorting` value is then NEITHER restored NOR saved — restoring
   * it would make the column-header arrow show a sort the BE query never
   * received (the persistence restore bypasses `onSortChange`), so the table
   * VISUALLY claims "sorted by Status" while the API still returns the
   * default order (prod incident 2026-05-17: campaign-targets table showed a
   * Status down-arrow but rows came back createdAt-ordered). `defaultSorting`
   * (which the parent derives from the same URL/state) is the sole source.
   */
  urlControlledSorting?: boolean
  /** Skip row selection persistence (when externally controlled) */
  isControlled?: boolean
  /** Cleanup legacy keys on load */
  sanitizePersistedState?: (state: PersistedTableState) => PersistedTableState
}

export function useTablePersistence(config: UseTablePersistenceConfig) {
  const {
    prefix,
    columnDefaults,
    filterDefaults,
    defaultSorting = [],
    urlControlledSorting = false,
    isControlled = false,
    sanitizePersistedState,
  } = config

  const pinnedLeft = columnDefaults?.pinned?.left ?? []
  const pinnedRight = columnDefaults?.pinned?.right ?? []

  // Initialize with defaults (no localStorage read during initial render to avoid hydration mismatch)
  const [mounted, setMounted] = React.useState(false)
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(columnDefaults?.order ?? [])
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(columnDefaults?.visibility ? { ...columnDefaults.visibility } : {})
  const [sorting, setSorting] = React.useState<SortingState>(defaultSorting)
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = React.useState("")
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({})
  const [filterVisibility, setFilterVisibility] = React.useState<Record<string, boolean>>(filterDefaults?.visibility ? { ...filterDefaults.visibility } : {})
  const [filterOrder, setFilterOrder] = React.useState<string[]>(filterDefaults?.order ? [...filterDefaults.order] : [])
  const [advancedSearchState, setAdvancedSearchState] = React.useState<unknown>(null)

  // Load from localStorage after mount (client-only)
  React.useEffect(() => {
    setMounted(true)

    const raw: PersistedTableState = {}

    // Load column order — enforce pinned positions
    const storedOrder = localStorage.getItem(`${prefix}-column-order`)
    if (storedOrder) {
      try {
        const parsed = JSON.parse(storedOrder) as string[]
        // Strip out pinned columns
        const allPinned = [...pinnedLeft, ...pinnedRight]
        const middle = parsed.filter((col: string) => !allPinned.includes(col))
        if (middle.length > 0) {
          raw.columnOrder = [...pinnedLeft, ...middle, ...pinnedRight]
        }
      } catch {}
    }

    // Load column sizing
    const storedSizing = localStorage.getItem(`${prefix}-column-sizing`)
    if (storedSizing) {
      try { raw.columnSizing = JSON.parse(storedSizing) } catch {}
    }

    // Load column visibility
    const storedVisibility = localStorage.getItem(`${prefix}-column-visibility`)
    if (storedVisibility) {
      try { raw.columnVisibility = JSON.parse(storedVisibility) } catch {}
    }

    // Load filter visibility
    const storedFilterVisibility = localStorage.getItem(`${prefix}-filter-visibility`)
    if (storedFilterVisibility) {
      try { raw.filterVisibility = JSON.parse(storedFilterVisibility) } catch {}
    }

    // Load filter order
    const storedFilterOrder = localStorage.getItem(`${prefix}-filter-order`)
    if (storedFilterOrder) {
      try { raw.filterOrder = JSON.parse(storedFilterOrder) } catch {}
    }

    // FIX: Bug 4 — row selection must NOT persist across page loads (data-safety:
    // a stale selection from a prior session can trigger accidental bulk-deletes).
    // Drop the key unconditionally on mount; any pre-existing value is discarded.
    localStorage.removeItem(`${prefix}-row-selection`)

    // Load column filters
    const storedFilters = localStorage.getItem(`${prefix}-column-filters`)
    if (storedFilters) {
      try { raw.columnFilters = stripSelectAllSentinel(JSON.parse(storedFilters)) } catch {}
    }

    // Load sorting — skipped entirely when sorting is URL/parent-controlled
    // (see `urlControlledSorting` doc above). Restoring a stale localStorage
    // sort there desyncs the header arrow from the actual BE query.
    if (!urlControlledSorting) {
      const storedSorting = localStorage.getItem(`${prefix}-sorting`)
      if (storedSorting) {
        try { raw.sorting = JSON.parse(storedSorting) } catch {}
      }
    }

    // Load global filter
    const storedGlobalFilter = localStorage.getItem(`${prefix}-global-filter`)
    if (storedGlobalFilter) {
      raw.globalFilter = storedGlobalFilter
    }

    // Override with ephemeral AI navigation context (consumed once, then deleted)
    const aiGlobalFilter = sessionStorage.getItem("ai-nav-global-filter")
    if (aiGlobalFilter !== null) {
      sessionStorage.removeItem("ai-nav-global-filter")
      raw.globalFilter = aiGlobalFilter
    }
    const aiColumnFilters = sessionStorage.getItem("ai-nav-column-filters")
    if (aiColumnFilters) {
      sessionStorage.removeItem("ai-nav-column-filters")
      try { raw.columnFilters = stripSelectAllSentinel(JSON.parse(aiColumnFilters)) } catch {}
    }

    // Load advanced search
    const storedAdvancedSearch = localStorage.getItem(`${prefix}-advanced-search`)
    if (storedAdvancedSearch) {
      try { raw.advancedSearch = JSON.parse(storedAdvancedSearch) } catch {}
    }

    // Allow caller to sanitize (e.g., remove legacy keys)
    const state = sanitizePersistedState ? sanitizePersistedState(raw) : raw

    // Apply loaded state
    if (state.columnOrder) setColumnOrder(state.columnOrder)
    if (state.columnSizing) setColumnSizing(state.columnSizing)
    if (state.columnVisibility) setColumnVisibility(prev => ({ ...prev, ...state.columnVisibility }))
    if (state.filterVisibility) setFilterVisibility(state.filterVisibility)
    if (state.filterOrder) setFilterOrder(state.filterOrder)
    if (state.rowSelection && !isControlled) setInternalRowSelection(state.rowSelection)
    if (state.columnFilters) setColumnFilters(stripSelectAllSentinel(state.columnFilters))
    if (state.sorting) setSorting(state.sorting)
    if (state.globalFilter) setGlobalFilter(state.globalFilter)
    if (state.advancedSearch) setAdvancedSearchState(state.advancedSearch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix, isControlled])

  // Save to localStorage effects (only after mount)
  React.useEffect(() => {
    if (mounted) localStorage.setItem(`${prefix}-column-visibility`, JSON.stringify(columnVisibility))
  }, [columnVisibility, mounted, prefix])

  React.useEffect(() => {
    if (mounted) localStorage.setItem(`${prefix}-column-order`, JSON.stringify(columnOrder))
  }, [columnOrder, mounted, prefix])

  React.useEffect(() => {
    if (mounted) localStorage.setItem(`${prefix}-column-sizing`, JSON.stringify(columnSizing))
  }, [columnSizing, mounted, prefix])

  React.useEffect(() => {
    if (mounted && Object.keys(filterVisibility).length > 0) localStorage.setItem(`${prefix}-filter-visibility`, JSON.stringify(filterVisibility))
  }, [filterVisibility, mounted, prefix])

  React.useEffect(() => {
    if (mounted && filterOrder.length > 0) localStorage.setItem(`${prefix}-filter-order`, JSON.stringify(filterOrder))
  }, [filterOrder, mounted, prefix])

  // FIX: Bug 4 — row selection is intentionally NOT written to localStorage.
  // Persisting selection is a data-safety risk (stale bulk-delete targets survive reload).
  // The removeItem on mount above cleans up any legacy key written by older builds.

  React.useEffect(() => {
    if (mounted) {
      localStorage.setItem(`${prefix}-column-filters`, JSON.stringify(columnFilters))
      // Notify parent hooks about filter changes for server-side filtering
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('recipients-filter-change'))
        window.dispatchEvent(new CustomEvent('table-column-filter-change', {
          detail: { prefix, columnFilters },
        }))
      }
    }
  }, [columnFilters, mounted, prefix])

  React.useEffect(() => {
    if (mounted) {
      // Skip the localStorage write when sorting is URL/parent-controlled —
      // the parent's URL/state is the source of truth; a persisted copy only
      // risks desyncing a later mount.
      if (!urlControlledSorting) {
        localStorage.setItem(`${prefix}-sorting`, JSON.stringify(sorting))
      }
      // Notify parent hooks about sort changes for server-side sorting
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('recipients-sort-change'))
        window.dispatchEvent(new CustomEvent('table-sorting-change', {
          detail: { prefix, sorting },
        }))
      }
    }
  }, [sorting, mounted, prefix, urlControlledSorting])

  React.useEffect(() => {
    if (mounted) {
      localStorage.setItem(`${prefix}-global-filter`, globalFilter)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('table-global-filter-change', {
          detail: { prefix, globalFilter },
        }))
      }
    }
  }, [globalFilter, mounted, prefix])

  // A SEARCH TERM MUST NOT SURVIVE LEAVING THE PAGE (#search-retention).
  // Typing a search persists it (the effect above), so returning to a list later
  // re-applied the previous term: the user landed on a silently pre-filtered
  // list they had to notice and clear by hand. A search is an intent for RIGHT
  // NOW — on a fresh visit the box must be empty and the user types again.
  //
  // WHY HERE, AND NOT BY REFUSING TO SEED ON MOUNT: that was tried and reverted
  // (see lib/__tests__/table-filters-not-restored.guard.spec.ts). These keys are
  // the page<->table CHANNEL — a controlled page writes the term during render
  // and the table reads it back — so a table that declines to seed overwrites
  // the page's value with its own empty state and dispatches a change event the
  // page answers by clearing, which silently broke shareable `?search=` links.
  //
  // Clearing on UNMOUNT keeps every mount-time behaviour byte-identical (the
  // seed, the write, the event, the `?search=` handoff) and only drops the key
  // once the table is going away, so nothing is left behind to resurrect on the
  // next visit. Deliberately NOT keyed on `globalFilter` — this must run when
  // the table unmounts, not on every keystroke.
  React.useEffect(() => {
    return () => {
      try {
        localStorage.removeItem(`${prefix}-global-filter`)
      } catch {}
    }
  }, [prefix])

  React.useEffect(() => {
    if (mounted && advancedSearchState !== null) localStorage.setItem(`${prefix}-advanced-search`, JSON.stringify(advancedSearchState))
  }, [advancedSearchState, mounted, prefix])

  // Listen for AI contextual navigation events (same-page filter updates)
  React.useEffect(() => {
    if (!mounted) return
    const handler = (e: Event) => {
      const ctx = (e as CustomEvent).detail
      if (!ctx) return
      if (ctx.columnFilters?.length) {
        setColumnFilters(stripSelectAllSentinel(ctx.columnFilters))
      }
      if (typeof ctx.globalFilter === "string") {
        setGlobalFilter(ctx.globalFilter)
      }
    }
    window.addEventListener("ai-nav-context", handler)
    return () => window.removeEventListener("ai-nav-context", handler)
  }, [mounted, prefix])

  // Listen for external filter clear events (e.g. "Clear All Filters" button in page header).
  // When the parent hook clears localStorage and dispatches this event, sync in-memory state.
  React.useEffect(() => {
    if (!mounted) return
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail || detail.prefix !== prefix) return
      // Sync column filters from event payload (typically empty array = clear all)
      if (Array.isArray(detail.columnFilters)) {
        setColumnFilters(stripSelectAllSentinel(detail.columnFilters))
      }
    }
    window.addEventListener("table-column-filter-change", handler)
    return () => window.removeEventListener("table-column-filter-change", handler)
  }, [mounted, prefix])

  // Listen for external global filter clear (when parent clears localStorage)
  React.useEffect(() => {
    if (!mounted) return
    const handler = () => {
      // Re-read from localStorage (which may have been cleared)
      const stored = localStorage.getItem(`${prefix}-global-filter`)
      const newValue = stored ?? ""
      if (newValue !== globalFilter) {
        setGlobalFilter(newValue)
      }
    }
    window.addEventListener("recipients-filter-change", handler)
    return () => window.removeEventListener("recipients-filter-change", handler)
  }, [mounted, prefix, globalFilter])

  // Reset functions
  const resetColumns = React.useCallback(() => {
    setColumnVisibility(columnDefaults?.visibility ? { ...columnDefaults.visibility } : {})
    setColumnOrder(columnDefaults?.order ?? [])
    setColumnSizing({})
    localStorage.removeItem(`${prefix}-column-visibility`)
    localStorage.removeItem(`${prefix}-column-order`)
    localStorage.removeItem(`${prefix}-column-sizing`)
  }, [prefix, columnDefaults])

  const resetFilters = React.useCallback(() => {
    setFilterVisibility(filterDefaults?.visibility ? { ...filterDefaults.visibility } : {})
    setFilterOrder(filterDefaults?.order ? [...filterDefaults.order] : [])
    setColumnFilters([])
    localStorage.removeItem(`${prefix}-filter-visibility`)
    localStorage.removeItem(`${prefix}-filter-order`)
    localStorage.removeItem(`${prefix}-column-filters`)
  }, [prefix, filterDefaults])

  return {
    mounted,
    // Column state
    columnOrder, setColumnOrder,
    columnSizing, setColumnSizing,
    columnVisibility, setColumnVisibility,
    // Sorting & filtering
    sorting, setSorting,
    columnFilters, setColumnFilters,
    globalFilter, setGlobalFilter,
    // Row selection (internal)
    internalRowSelection, setInternalRowSelection,
    // Filter bar
    filterVisibility, setFilterVisibility,
    filterOrder, setFilterOrder,
    // Advanced search
    advancedSearchState, setAdvancedSearchState,
    // Reset
    resetColumns,
    resetFilters,
    // Pinned columns info
    pinnedLeft, pinnedRight,
  }
}
